import './admin.scss';
import React, {useContext, useEffect, useState} from "react";
import {PlarCardType} from "../../../components/UI/Cards/Plar/PlarCard";
import {useNavigate, useParams} from "react-router-dom";
import {useQueryPrograms} from "../../../api/admin/useQueryPrograms";
import {useQueryCourses} from "../../../api/admin/useQueryCourses";
import {useQueryClient} from "@tanstack/react-query";
import {coursesSortingOptionsAdmin} from "../../../constants/ui/sortingOptions";
import {ISortingOption} from "../../../components/UI/SearchAndSort/SearchAndSort";
import Breadcrumbs from "../../../components/UI/Breadcrumbs/Breadcrumbs";
import {PortfolioContext} from "../../../hooks/Context/PortfolioContext";
import CourseCard from '../../../components/UI/Cards/Plar/CourseCard';
import Loading from "../../../components/UI/Loading/Loading";
import ComplexSearch from "../../../components/UI/ComplexSearch/ComplexSearch";
import {useMutateRecommendation} from "../../../api/normalization/useMutateRecommendation";

export interface ICourse {
  id: string,
  course_recommendation_id: string,
  code: string,
  description: string,
  matching_score: number,
  approved?: string,
  reviewed_at_date?: string,
  reviewer_name?: string,
  title: string,
  type?: string,
  cpl_eligible?: boolean
  payment_status: string,
  quiz_request?: {
    id: string,
    due_date: string,
    completed: boolean
  },
  active_catalogue_version?: boolean
  updated_by_previous_recommendation?: boolean,
  price: number,
  program_when_added_cart?: {
    id : number,
    name: string,
  }
}

const AdminCourses = () => {
  const navigate = useNavigate();
  const {user_id, program_id, portfolio_id} = useParams();
  const [portfolio] = useContext(PortfolioContext);
  const queryClient = useQueryClient();
  const [searchValue, setSearchValue] = useState<string>('');
  const [sortingValue, setSortingValue] = useState<ISortingOption>(coursesSortingOptionsAdmin[4]);
  const [init, setInit] = useState<boolean>(false);
  const [showAllcourses, setShowAllcourses] = useState<boolean>(false);

  const {data: currProgram} = useQueryPrograms({
    portfolio_id: portfolio_id,
    program_id: program_id
  });

  const {data: courses} = useQueryCourses({
    portfolio_id: portfolio_id,
    program_id: program_id,
    searchValue,
    sortingValue: sortingValue.value,
    only_current_submission_courses: true,
    show_recommended: !showAllcourses
  });

  const {
    mutate: generateMatchScore,
    isPending
  } = useMutateRecommendation({userIsSoft: false})

  const getCardInfo = (course: ICourse) => {
    if (course.approved === null) {
      return {
        type: PlarCardType.Not_started,
        chicletText: undefined,
        statusText: undefined
      };
    }
    if (course.approved) {
      return {
        type: PlarCardType.Completed,
        chicletText: 'Approved',
        statusText: [`Approved on ${course.reviewed_at_date} by ${course.reviewer_name}`]
      };
    }
    return {
      type: PlarCardType.Rejected,
      chicletText: 'Rejected',
      statusText: [`Rejected on ${course.reviewed_at_date} by ${course.reviewer_name}`]
    };
  }

  const generateMatch = (courseId: string) => {
    generateMatchScore({
      data: {
        course_id: courseId,
        portfolio_id: portfolio_id
      }
    });
  }

  useEffect(() => {
    window.scroll(0, 0);
  }, [])

  useEffect(() => {
    if (searchValue.length > 2 || searchValue.length === 0) {
      queryClient.invalidateQueries({queryKey: ['courses']});
    }
  }, [searchValue])

  useEffect(() => {
    if (init) {
      queryClient.invalidateQueries({queryKey: ['courses']});
    } else {
      setInit(true);
    }
  }, [sortingValue, showAllcourses])

  return <div>
    <Loading loading={isPending}/>

    <div className={'admin-courses'}>
      <Breadcrumbs content={[
        {
          label: 'Applicants',
          link: `/cpl-admin`
        },
        {
          label: `${portfolio.portfolio_user_name}’s profile`,
          link: `/cpl-admin/${user_id}/${portfolio_id}`
        },
        {
          label: `${currProgram?.name}`,
          link: `/cpl-admin/${user_id}/${portfolio_id}/${program_id}`
        }
      ]}/>
      <div className={'admin-courses-title'}>
        {currProgram?.name}
      </div>
      <div className={'admin-courses-subtitle'}>
        {`${currProgram?.num_courses_recommended} courses recommended`}
      </div>

      <ComplexSearch
        search={{
          value: searchValue,
          onChange: (e) => setSearchValue(e.target.value),
          placeholder: 'Search Courses'
        }}
        sorting={{
          options: coursesSortingOptionsAdmin,
          value: sortingValue,
          onChange: (newSorting) => setSortingValue(newSorting)
        }}
        toggle={[{
          active: showAllcourses,
          onToggle: () => setShowAllcourses(!showAllcourses),
          label: 'Show all courses'
        }]}
      />

      {
        courses?.data?.courses && courses.data.courses.map((item: ICourse, index: number) =>
          <div key={index}>
            <CourseCard
              {...getCardInfo(item)}
              title={item.title}
              code={item.code}
              onClick={() => navigate(`/cpl-admin/${user_id}/${portfolio_id}/${program_id}/${item.code}`)}
              buttonText={'View supporting evidence'}
              buttonDisabled={!item.course_recommendation_id}
              match={`${Math.floor(item.matching_score * 100)}% match`}
              cpl_eligible={item.cpl_eligible}
              payment_status={
                (item?.payment_status === 'paid' || item?.payment_status === 'selected-for-payment')
                  ? item.payment_status
                  : ''
              }
              changePaymentStatus={() => {}}
              active_catalogue_version={item.active_catalogue_version}
              course_recommendation_id={item.course_recommendation_id}
              generateMatch={() => generateMatch(item.id)}
            />
          </div>)
      }
    </div>
  </div>
}

export default AdminCourses;